
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DocumentEntity, DocumentReference } from '@app/core/enumerations';
import { UploadFromContainer } from '@app/core/model';
import { DocumentReferenceApiService } from '@app/core/services';
import { LibPipeModule, ProductNames } from '@mynexus/mynexus-ui-lib';
import { SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { finalize } from 'rxjs';
import { AttachToAuthorizationApiService } from './services/attach-to-authorization-api.service';

@Component({
    selector: 'mnc-document-attach-to-authorization-form',
    imports: [
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    MultiSelectModule,
    InputTextModule,
    LibPipeModule,
    ProgressSpinnerModule
],
    providers: [
        DocumentReferenceApiService,
        AttachToAuthorizationApiService
    ],
    templateUrl: './attach-to-authorization-form.component.html',
    styleUrls: ['./attach-to-authorization-form.component.scss']
})
export class AttachToAuthorizationFormComponent implements OnChanges {

  // Modal Display Information
  @Input() memberDob!: string;
  @Input() memberFullName!: string;
  @Input() memberSubscriberNumber!: string; // Displayed as MemberId

  // Authorization Verification Information
  @Input() memberId!: string;
  @Input() tenantLobProductId: string;
  @Input() product: string;
  // These two should only be checked when in a context-less environment.
  @Input() memberTenantId: string;
  @Input() memberLobId: string

  // Upload supporting information
  @Input() faxReceivedDateTime!: string;
  @Input() documentStorageLocation!: string;
  @Input() sourceFileLocation!: string;
  @Input() sourceContainer!: string;

  // Outputs
  @Output() cancelClicked = new EventEmitter<void>();
  @Output() completedUpload = new EventEmitter<string>(null);
  @Output() errorOccurred = new EventEmitter<object>(null);
  @Output() initCompleted = new EventEmitter<boolean>(false);

  get authorizationNumber(): AbstractControl<string> {
    return this.form.get('authorizationNumber');
  }

  get description(): AbstractControl<string> {
    return this.form.get('description');
  }

  get documentType(): AbstractControl<string[]> {
    return this.form.get('documentType');
  }

  readonly MAX_LENGTH_AUTH_NUMBER = 50;
  readonly MIN_LENGTH_AUTH_NUMBER = 15;
  readonly MAX_LENGTH_DESCRIPTION = 40;

  form: FormGroup;
  documentTypes: SelectItem[] = [];
  authNotFound = false;
  isLoading = false;
  isSearchLoading = false;
  missingContextFlag = false; // Used to support "contextless" sources, such as the A&G Inbound Fax Flow
  allowAuthNumberChange = true; // Also used in contextless sources - user cannot change Auth Number after a Search is performed
  previouslySearched: string;

  private readonly formBuilder = inject(FormBuilder);
  private readonly documentReferenceApiService = inject(
    DocumentReferenceApiService
  );
  private readonly destroyedRef = inject(DestroyRef);
  private readonly attachToAuthorizationApiService = inject(AttachToAuthorizationApiService);

  ngOnChanges(changes: SimpleChanges): void {
    this.form = this.formBuilder.group({
      authorizationNumber: [
        '',
        [
          Validators.required,
          Validators.minLength(this.MIN_LENGTH_AUTH_NUMBER),
          Validators.maxLength(this.MAX_LENGTH_AUTH_NUMBER),
        ],
      ],
      documentType: ['', [Validators.required]],
      description: ['', [Validators.maxLength(this.MAX_LENGTH_DESCRIPTION)]],
    });

    // Since we may enter this component from a context-less source, we need to check for these fields before blindly firing off the References call
    if (this.changeHasValue(changes['tenantLobProductId']) && this.changeHasValue(changes['product'])) {
      this.getReferences(this.product, this.tenantLobProductId);
    }
    else {
      this.missingContextFlag = true;
      this.initCompleted.emit(true);
    }
  }

  getReferences(product: string, tenantLobProductId: string) :void {
    // Remove product comparison when 229709 is implemented
    // TODO: Remove PAC Commercial after LOB migration
    const { reference, entity } = (product === ProductNames.PACIM || product === ProductNames.PacCommercial)
      ? { reference: DocumentReference.DocumentType, entity: DocumentEntity.Attach }
      : { reference: DocumentReference.Upload, entity: DocumentEntity.None };

    this.documentReferenceApiService
      .getDocumentTypes(tenantLobProductId, reference, entity)
      .pipe(
        takeUntilDestroyed(this.destroyedRef),
        finalize(() => this.initCompleted.emit(true))
      )
      .subscribe({
        next: (documentTypes) => {
          this.documentTypes = documentTypes.map((x) => ({
            label: x.description,
            value: x.description,
          }));
        },
        error: () =>
          this.errorOccurred.emit({
            title: 'Unable to Load Document Types',
            message:
              'There was an issue loading the document types. Please try again later.',
          }),
      });
  }

  search(): void {
    // reset validation message, set loading indicator
    this.authNotFound = false;
    this.isSearchLoading = true;

    // Check if Auth Exists, and return Tenant, LoB, Product info
    this.attachToAuthorizationApiService.getAuthorizationContext(this.memberTenantId, this.memberLobId, this.authorizationNumber.value, )
    .pipe(
      takeUntilDestroyed(this.destroyedRef)
    )
    .subscribe({
      next: res => {
        if (res) {
          this.getReferences(res.shortName, res.tenantLobProductId);
          this.tenantLobProductId = res.tenantLobProductId; // set for later attach call
          this.missingContextFlag = false;
          this.allowAuthNumberChange = false;
        }
        else {
          // toggle error message, auth not found.
          this.authNotFound = true;
          this.previouslySearched = this.authorizationNumber.value;
          this.isSearchLoading = false;
          this.missingContextFlag = true;
          this.allowAuthNumberChange = true;
        }
      },
      error: () => {
        this.authNotFound = true;
        this.previouslySearched = this.authorizationNumber.value;
        this.isSearchLoading = false;
        this.missingContextFlag = true;
        this.allowAuthNumberChange = true;
      }
    });
  }

  submitClick(): void {
    // reset validation message, set loading indicator
    this.authNotFound = false;
    this.isLoading = true;

    // Check if Auth is Valid
    this.attachToAuthorizationApiService.isValidAuthorization(this.authorizationNumber.value, this.tenantLobProductId, this.memberId)
    .pipe(
      takeUntilDestroyed(this.destroyedRef)
    )
    .subscribe({
      next: res => {
        if (res) {
          // If valid, send the upload request
          this.attachToAuthorization();
        }
        else {
          // toggle error message, auth not found.
          this.authNotFound = true;
          this.previouslySearched = this.authorizationNumber.value;
          this.isLoading = false;
        }
      },
      error: () => {
        this.authNotFound = true;
        this.previouslySearched = this.authorizationNumber.value;
        this.isLoading = false;
      }
    });
  }

  cancelClick(): void {
    this.cancelClicked.emit();
  }

  isValid(formControlName: string): boolean {
    return (
      this.form.get(formControlName).touched &&
      this.form.get(formControlName).invalid
    );
  }

  private attachToAuthorization(): void {
    // Create basic object
    const uploadFromContainer: UploadFromContainer = {
      sourceContainer: this.sourceContainer,
      sourceFileName: this.sourceFileLocation,
      tenantLobProductId: this.tenantLobProductId,
      file: {
        description: this.description.value,
        authorizationId: '00000000-0000-0000-0000-000000000000', // This will be filled in on the API, but cannot be null
        authorizationNumber: this.authorizationNumber.value,
        documentSource: 'Inbound Faxes',
        documentTypes: this.documentType.value,
        dateTimeFaxReceived: this.faxReceivedDateTime,
        documentFileName: this.documentStorageLocation
      }
    };

    // Call attach method
    this.attachToAuthorizationApiService.attachFileToAuth(uploadFromContainer)
    .pipe(
      takeUntilDestroyed(this.destroyedRef),
      finalize(() => this.isLoading = false)
    )
    .subscribe({
      next: () => {
        this.completedUpload.emit(uploadFromContainer.file.authorizationNumber);
      },
      error: () => {
        this.errorOccurred.emit({
          title: 'Unable to Attach Fax to Authorization',
          message:
            'Unable to Attach Fax to Authorization. An error has occurred. Please try again later.',
        })
      }
    });
  }

  // Helper function to make sure the change is not null or undefined, or any other "falsy" value
  private changeHasValue(change: SimpleChange): boolean {
    if (change && change.currentValue)
      return true;
    else
      return false;
  }
}
